import { useEffect, useRef } from 'react';

import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { watchNativeAppState } from '#features/network/utils/watchNativeAppState';
import { onWsReconnect, sendWsMessage } from '#features/realtime/hooks/useRealtimeNotifications';
import { envConfig } from '#constants/envConfig';

/**
 * Сколько человек может ничего не трогать при открытой вкладке, прежде чем считать, что он отошёл.
 * Пять минут — чтение длинного поста или разговор рядом с компьютером в это укладываются.
 */
const IDLE_AFTER_MS = 5 * 60_000;

/**
 * Свернул вкладку или приложение — считаем, что он не смотрит, немедленно.
 *
 * <p>Раньше здесь стояли две минуты: свёрнутая вкладка это ведь и второй монитор, и музыка, и
 * быстрое переключение. Но пока сервер считает человека смотрящим, он не шлёт ни пуш, ни письмо, —
 * а всплывашку в свёрнутой вкладке никто не видит. Получались три минуты полной тишины: две здесь
 * и ещё минута отложенной очереди на сервере.
 *
 * <p>Теперь цену за поспешность платит только пуш, и она невелика: уведомление снимается, как
 * только человек прочитает разговор. Письмо и сообщение в Discord защищены на сервере отдельным
 * порогом — им нужно, чтобы человек не смотрел на экран уже пару минут.
 */
/** Чаще раза в полминуты слать «я тут» незачем — состояние на сервере от этого не меняется. */
const ACTIVE_THROTTLE_MS = 30_000;

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

/**
 * Сообщает серверу, за экраном человек или отошёл.
 * <p>
 * Само соединение об этом ничего не говорит: клиент пингует каждые 25 секунд, пока открыта
 * вкладка, поэтому брошенный на весь день ноутбук выглядит точно так же, как работающий человек.
 * Без этого сигнала «отошёл» показать нечем — и, что важнее, уведомления о сообщениях таким людям
 * не уходят: teamplay-notification глушит push, email и Discord DM всем, кого считает онлайн.
 */
export const useIdleReporter = () => {
  const isAuth = useIsAuth();
  const stateRef = useRef<'active' | 'idle' | null>(null);
  const lastSentAtRef = useRef(0);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isAuth || typeof window === 'undefined') {
      return;
    }

    let disposed = false;
    let appActive = !envConfig.IS_NATIVE;
    const isForeground = () => appActive && document.visibilityState !== 'hidden';

    const report = (state: 'active' | 'idle', force = false) => {
      if (disposed) return;
      const now = Date.now();
      if (
        !force &&
        state === stateRef.current &&
        now - lastSentAtRef.current < ACTIVE_THROTTLE_MS
      ) {
        return;
      }

      stateRef.current = state;
      lastSentAtRef.current = now;
      sendWsMessage({ action: 'presence', state });
    };

    const scheduleIdle = () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }

      timerRef.current = null;
      if (!isForeground()) {
        // Немедленно и в обход троттлинга: между «свернул» и «ему написали» может пройти
        // меньше секунды, и отложенная на такт отправка вернула бы ту же тишину.
        report('idle', true);
        return;
      }
      timerRef.current = window.setTimeout(() => report('idle'), IDLE_AFTER_MS);
    };

    const onActivity = () => {
      if (!isForeground()) return;
      report('active');
      scheduleIdle();
    };

    const onVisibilityChange = () => {
      if (isForeground()) {
        onActivity();
        return;
      }
      // Вкладку свернули — человек больше не смотрит, и сервер должен узнать это сейчас.
      scheduleIdle();
    };

    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, onActivity, { passive: true });
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Переподключение поднимает сессию заново, и серверу она известна как активная — состояние
    // нужно послать ещё раз, иначе отошедший человек после реконнекта снова станет зелёным.
    const offReconnect = onWsReconnect(() =>
      report(isForeground() ? (stateRef.current ?? 'active') : 'idle', true)
    );

    // В приложении на телефоне видимость страницы — не единственный сигнал: WebView может
    // остаться «видимым» при сворачивании приложения кнопкой «домой». Слушаем и системное
    // состояние приложения, иначе человек с открытым чатом уйдёт в другое приложение и
    // останется для сервера сидящим за экраном — то есть без уведомлений.
    const detachAppState = watchNativeAppState((isActive) => {
      if (appActive === isActive) return;
      appActive = isActive;
      onVisibilityChange();
    });

    if (isForeground()) report('active', true);
    scheduleIdle();

    return () => {
      disposed = true;
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, onActivity);
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
      offReconnect();
      detachAppState();
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      stateRef.current = null;
    };
  }, [isAuth]);
};
